/**
 * SearchConsoleBand — the composer's Search Console source (lot H step 13, UI-SPEC §2.13),
 * open to every tenant (no feature flag, owner 2026-10-23).
 *
 * Connect, the property line, the query list with one proposed prompt per
 * query, and the honest non-ready states (never a 0, an empty table or a stale list).
 * The draft starts as the checked rows' proposals and is editable (`gscDraftText`); « Vérifier » is the
 * composer's, through lot B's normal review and save. State: searchConsoleStore.ts.
 *
 * @see B-popup-frontend.md step 5, Files and props § SearchConsoleBand
 * @see docs/plans/prompt-studio-build/H-search-console.md step 13
 */
import { useEffect, useRef } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n/format';
import type { GscLine } from './searchConsoleModel';
import { gscDraftText, gscView, isBrandProperty, propertyName, propertyType } from './searchConsoleModel';
import { authorize, chooseProperty, disconnectGsc, dispatchGsc, openBand, openPicker, retryProposals, toggleRow, useGsc } from './searchConsoleStore';
import { normalizeDomain } from '@traaker/shared';

export interface SearchConsoleBandProps {
  brandId?: string;
  /** The brand's site, for the « votre domaine » token of the picker. */
  brandUrl?: string;
  isAgency?: boolean;
}

export function SearchConsoleBand({ brandId, brandUrl, isAgency }: SearchConsoleBandProps) {
  if (!brandId) return null;
  return <SearchConsoleLive brandId={brandId} brandUrl={brandUrl} isAgency={!!isAgency} />;
}

const LINE_KEY: Record<GscLine, string> = {
  callbackError: 'promptStudio.gsc.callbackError',
  scopeMissing: 'promptStudio.gsc.scopeMissing',
  wrongUser: 'promptStudio.gsc.wrongUser',
};

const WARN = '#92400e';
const AMBER = '#b45309';

/** Where Google sends the user back: this page, minus a previous `gsc` result. */
function returnPathHere(): string {
  const u = new URL(window.location.href);
  u.searchParams.delete('gsc');
  return u.pathname + u.search;
}

function GscIcon({ size }: { size: number }) {
  return (
    <Box
      component="img"
      src="/icons/search-console.svg"
      alt=""
      aria-hidden="true"
      sx={{ width: size, height: size, flex: `0 0 ${size}px`, display: 'block' }}
      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

const linkSx = {
  border: 0, background: 'none', font: 'inherit', fontSize: 12, fontWeight: 600, color: '#6d28d9',
  cursor: 'pointer', px: 0.625, py: 0.25, borderRadius: 0.75, flex: '0 0 auto',
  '&:hover': { bgcolor: 'rgba(124,58,237,.08)' },
  '&:disabled': { color: 'text.disabled', cursor: 'default' },
} as const;

function SearchConsoleLive({ brandId, brandUrl, isAgency }: { brandId: string; brandUrl?: string; isAgency: boolean }) {
  const { t } = useTranslation();
  const s = useGsc(brandId);

  // Once per band open. StrictMode re-runs the effect on the same instance (the ref
  // survives), so the status, the import and the callback line are not doubled.
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (opened.current === brandId) return;
    opened.current = brandId;
    void openBand(brandId);
  }, [brandId]);

  const view = gscView(s);
  const draft = gscDraftText(s);
  const onAuthorize = () => void authorize(brandId, returnPathHere());
  // « Changer »: the picker over the account's properties; with none readable (or the
  // status was revoked meanwhile), Google again to authorize another account.
  const onChange = () =>
    void openPicker(brandId).then((r) => {
      if (r === 'none') onAuthorize();
    });

  const who = isAgency ? (
    <Box sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1.125 }}>{t('promptStudio.gsc.who')}</Box>
  ) : null;

  // Wherever we hold a token or the account's email (privacy policy: kept « jusqu'à la déconnexion »).
  const disconnectButton = (
    <Box
      component="button"
      type="button"
      data-gsc-disconnect=""
      onClick={() => void disconnectGsc(brandId)}
      disabled={s.statusLoading || s.authorizing}
      sx={linkSx}
    >
      {t('promptStudio.gsc.disconnect')}
    </Box>
  );
  const disconnectLine = <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>{disconnectButton}</Box>;

  const head = (siteUrl: string) => (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.875, fontSize: 12, color: 'text.disabled',
        pb: 1.125, mb: 1.125, borderBottom: '1px solid', borderColor: 'divider',
      }}
    >
      <GscIcon size={15} />
      <Box component="b" sx={{ fontWeight: 600, fontSize: 12.5, color: 'text.primary' }}>{propertyName(siteUrl)}</Box>
      {t(propertyType(siteUrl) === 'domain' ? 'promptStudio.gsc.type.domain' : 'promptStudio.gsc.type.prefix')}
      <Box component="button" type="button" onClick={onChange} disabled={s.authorizing} sx={{ ...linkSx, ml: 'auto' }}>
        {t('promptStudio.gsc.change')}
      </Box>
      {disconnectButton}
    </Box>
  );

  const offRow = (text: string, warn: boolean, action: string, onClick: () => void, primary = true) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375 }}>
      <GscIcon size={22} />
      <Box sx={{ fontSize: 13, lineHeight: 1.4, color: warn ? WARN : 'text.primary', minWidth: 0 }}>{text}</Box>
      <Button
        variant={primary ? 'contained' : 'outlined'}
        size="small"
        onClick={onClick}
        disabled={s.authorizing}
        sx={{ ml: 'auto', flex: '0 0 auto' }}
      >
        {action}
      </Button>
    </Box>
  );

  const spinner = (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <CircularProgress size={18} />
    </Box>
  );

  let body: React.ReactNode = null;
  switch (view.kind) {
    case 'loading':
      body = spinner;
      break;
    case 'unavailable':
      break; // the error line says it
    case 'off':
      body = <>{offRow(t('promptStudio.gsc.connect'), false, t('promptStudio.gsc.authorize'), onAuthorize)}{who}</>;
      break;
    case 'revoked':
      body = <>{offRow(t('promptStudio.gsc.revoked'), true, t('promptStudio.gsc.reconnect'), onAuthorize)}{disconnectLine}{who}</>;
      break;
    case 'accessLost':
      body = <>{offRow(t('promptStudio.gsc.accessLost'), true, t('promptStudio.gsc.change'), onChange)}{disconnectLine}{who}</>;
      break;
    case 'picker': {
      const mine = view.candidates.map((c) => isBrandProperty(c.siteUrl, brandUrl));
      const domain = normalizeDomain(brandUrl);
      body = (
        <>
          <Box sx={{ fontSize: 12.5, mb: 1.125, lineHeight: 1.4 }}>
            {mine.some(Boolean) || !domain
              ? t('promptStudio.gsc.ambiguous')
              : t('promptStudio.gsc.noneMatches', { domain })}
          </Box>
          {view.candidates.map((c, i) => (
            <Box
              key={c.siteUrl}
              component="button"
              type="button"
              data-gsc-property={c.siteUrl}
              disabled={!!s.choosing}
              onClick={() => void chooseProperty(brandId, c.siteUrl)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
                border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 1,
                px: 1.25, py: 0.875, font: 'inherit', fontSize: 13, color: 'text.primary', cursor: 'pointer',
                '& + &': { mt: 0.625 },
                '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,.06)' },
              }}
            >
              <Box component="span" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {propertyName(c.siteUrl)}
              </Box>
              {mine[i] && (
                <Box component="span" sx={{ flex: '0 0 auto', fontSize: 10.5, fontWeight: 600, color: '#047857', bgcolor: 'rgba(16,185,129,.14)', borderRadius: 999, px: 0.875, py: 0.25 }}>
                  {t('promptStudio.gsc.yourDomain')}
                </Box>
              )}
              {s.choosing === c.siteUrl && <CircularProgress size={11} />}
              <Box component="span" sx={{ ml: 'auto', fontSize: 10.5, color: 'text.disabled', flex: '0 0 auto' }}>
                {t(propertyType(c.siteUrl) === 'domain' ? 'promptStudio.gsc.type.domain' : 'promptStudio.gsc.type.prefix')}
              </Box>
            </Box>
          ))}
          <Box component="button" type="button" onClick={onAuthorize} disabled={s.authorizing} sx={{ ...linkSx, mt: 0.5, pt: 0.625 }}>
            {t('promptStudio.gsc.otherAccount')}
          </Box>
          {disconnectLine}
          {who}
        </>
      );
      break;
    }
    case 'importing':
      body = <>{head(view.siteUrl)}{spinner}</>;
      break;
    case 'connected':
      body = head(view.siteUrl);
      break;
    case 'empty':
      body = (
        <>
          {head(view.siteUrl)}
          {offRow(t('promptStudio.gsc.empty'), true, t('promptStudio.gsc.changeProperty'), onChange, false)}
        </>
      );
      break;
    case 'ready': {
      const on = new Set(s.checked);
      const withText = view.rows.filter((r) => s.proposals[r.query]);
      const allOn = withText.length > 0 && withText.every((r) => on.has(r.query));
      body = (
        <>
          {head(view.siteUrl)}
          {s.failedSeeds.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, color: AMBER, mb: 1 }}>
              {t('promptStudio.gsc.proposalsFailed')}
              <Box component="button" type="button" onClick={() => void retryProposals(brandId)} disabled={s.proposing.length > 0} sx={linkSx}>
                {t('promptStudio.gsc.retry')}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1, px: 0.25, pb: 0.625, borderBottom: '1px solid', borderColor: 'divider',
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary',
            }}
          >
            <Box component="span" sx={{ flex: '0 0 15px' }} />
            <Box component="span" sx={{ flex: '0 0 200px' }}>{t('promptStudio.gsc.colQuery')}</Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{t('promptStudio.gsc.colPrompt')}</Box>
            <Box component="span" sx={{ flex: '0 0 80px', textAlign: 'right' }}>{t('promptStudio.gsc.colImpressions')}</Box>
          </Box>
          {view.rows.map((r) => {
            const checked = on.has(r.query);
            const busy = s.proposing.includes(r.query);
            const proposal = s.proposals[r.query];
            const dim = checked ? 1 : 0.42;
            return (
              <Box
                key={r.query}
                component="label"
                data-gsc-row={r.query}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 0.25, py: 0.75, cursor: 'pointer',
                  '& + &': { borderTop: '1px solid', borderColor: 'divider' },
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={checked}
                  disabled={busy || proposal === null}
                  onChange={() => void toggleRow(brandId, r.query)}
                  aria-label={proposal || r.query}
                  sx={{ flex: '0 0 15px', m: 0, accentColor: '#7c3aed', cursor: 'pointer' }}
                />
                <Box component="span" title={r.query} sx={{ flex: '0 0 200px', fontSize: 12.5, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: dim }}>
                  {r.query}
                </Box>
                <Box component="span" sx={{ flex: '0 0 auto', color: 'text.disabled', fontSize: 11, lineHeight: 1 }}>→</Box>
                <Box
                  component="span"
                  title={proposal || undefined}
                  sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: dim }}
                >
                  {busy ? (
                    <CircularProgress size={11} />
                  ) : proposal === null ? (
                    <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>{t('promptStudio.gsc.noProposal')}</Box>
                  ) : (
                    proposal
                  )}
                </Box>
                <Box component="span" sx={{ flex: '0 0 80px', textAlign: 'right', fontSize: 11.5, color: 'text.disabled', fontVariantNumeric: 'tabular-nums', opacity: dim }}>
                  {formatNumber(r.impressions)}
                </Box>
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125, mt: 1.125, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box
              component="button"
              type="button"
              disabled={withText.length === 0}
              onClick={() => dispatchGsc(brandId, { type: 'checkAll', on: !allOn })}
              sx={linkSx}
            >
              {t(allOn ? 'promptStudio.gsc.uncheckAll' : 'promptStudio.gsc.checkAll')}
            </Box>
            <Box component="span" sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {t('promptStudio.gsc.kept', { k: s.checked.length, n: view.rows.length })}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box component="span" title={t('promptStudio.gsc.windowTitle')} sx={{ fontSize: 10.5, color: 'text.disabled' }}>
              {t('promptStudio.gsc.window')}
            </Box>
          </Box>
        </>
      );
      break;
    }
  }

  return (
    <Box sx={{ px: 0.5 }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.25, bgcolor: '#fbfbfd', px: 1.5, py: 1.375 }}>
        {s.line && <Box sx={{ fontSize: 12, color: AMBER, mb: 1 }}>{t(LINE_KEY[s.line])}</Box>}
        {s.error === 'importLimited' && <Box sx={{ fontSize: 12, color: AMBER, mb: 1 }}>{t('promptStudio.gsc.importLimited')}</Box>}
        {s.error === 'unavailable' && <Box sx={{ fontSize: 12, color: AMBER, mb: 1 }}>{t('promptStudio.gsc.unavailable')}</Box>}
        {body}
      </Box>
      {draft !== null && (
        <Box
          component="textarea"
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => dispatchGsc(brandId, { type: 'editDraft', text: e.target.value })}
          placeholder={t('promptStudio.draft.placeholder')}
          data-gsc-draft=""
          sx={{
            width: '100%', mt: 1.5, minHeight: 142, border: '1px solid #7c3aed', borderRadius: 1.25, px: 1.5, py: 1.375,
            fontFamily: 'inherit', fontSize: 14, lineHeight: 1.85, fontWeight: 500, bgcolor: 'background.paper',
            color: 'text.primary', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      )}
    </Box>
  );
}
